import SEO from "@/components/SEO";
import { ASSETS, LINKEDIN_URL } from "@/lib/api";
import { Linkedin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function AboutAuthor() {
    return (
        <main data-testid="author-page" className="text-white pt-32 pb-24">
            <SEO
                title="About | Chandrashekar Bhat, Founder — Purposeful, Inc."
                description="Chandrashekar Bhat is the founder of Purposeful, Inc. and the author of the flagship book. Operator, writer and convener working at the intersection of entrepreneurship and sustainability."
                path="/about"
                keywords="Chandrashekar Bhat, buildwithbhat, founder of Purposeful Inc, impact entrepreneurship writer, The Purposeful Network founder"
                image="/assets/author.png"
                type="profile"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "Person",
                    "@id": "https://purposeful.inc/author#person",
                    "name": "Chandrashekar Bhat",
                    "url": "https://purposeful.inc/author",
                    "image": "https://purposeful.inc/assets/author.png",
                    "jobTitle": "Author, Operator, Convener",
                    "worksFor": { "@id": "https://purposeful.inc/#organization" },
                    "sameAs": ["https://www.linkedin.com/in/buildwithbhat/"],
                    "description": "Author of Purposeful, Inc. and founder of The Purposeful Network."
                }}
                breadcrumbs={[{ name: "Home", path: "/" }, { name: "About", path: "/about" }]}
            />

            <section className="container-x mx-auto max-w-7xl grid lg:grid-cols-2 gap-16 items-center">
                <div className="relative">
                    <img src={ASSETS.author} alt="Chandrashekar Bhat" loading="eager" decoding="async" fetchpriority="high" className="w-full rounded-2xl ring-1 ring-white/10" data-testid="author-portrait" />
                </div>
                <div>
                    <div className="overline mb-4">The founder</div>
                    <h1 className="font-display text-5xl md:text-6xl font-semibold tracking-tight">
                        Chandrashekar<br /><span className="text-[#D4AF37]">Bhat.</span>
                    </h1>
                    <p className="mt-3 text-zinc-400 font-mono text-xs uppercase tracking-widest">Founder, Purposeful, Inc.</p>
                    <p className="mt-6 text-zinc-300 text-lg leading-relaxed">
                        Operator, writer and convener working at the
                        intersection of entrepreneurship and sustainability,
                        chronicling the founders quietly redefining what a
                        modern business owes the world.
                    </p>
                    <p className="mt-4 text-zinc-400 leading-relaxed">
                        Over the last decade, he has spent time with founders
                        building across climate, health, education, livelihoods
                        and emerging tech. The ones doing real work, often
                        without the spotlight.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <a
                            href={LINKEDIN_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="author-linkedin-button"
                            className="btn-gold"
                        >
                            <Linkedin size={16} /> Connect on LinkedIn
                        </a>
                        <Link to="/contact" data-testid="author-contact-button" className="btn-ghost-gold">
                            Get in touch <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            <section className="container-x mx-auto max-w-5xl mt-32">
                <div className="overline mb-4">Why this book</div>
                <p className="font-display text-2xl md:text-3xl leading-snug text-zinc-200">
                    "I kept meeting founders whose answers to <em className="text-[#D4AF37]">'why are you doing this?'</em> stopped me cold.
                    Their stories deserved to be heard outside the rooms they
                    were told in. So I started writing."
                </p>
            </section>

            <section className="container-x mx-auto max-w-5xl mt-32" data-testid="author-linkedin-feature">
                <div className="overline mb-4">Featured on LinkedIn</div>
                <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight max-w-3xl">
                    Follow the writing in real time.
                </h2>
                <div className="mt-8 grid md:grid-cols-5 gap-6">
                    <div className="md:col-span-3 rounded-2xl border border-white/10 bg-black/40 p-8">
                        <div className="flex items-center gap-3">
                            <img
                                src={ASSETS.author}
                                alt="Chandrashekar Bhat"
                                loading="lazy"
                                decoding="async"
                                className="h-11 w-11 rounded-full object-cover ring-1 ring-white/10"
                            />
                            <div>
                                <div className="font-display text-base">Chandrashekar Bhat</div>
                                <div className="text-zinc-500 text-xs">Author, Purposeful, Inc. · buildwithbhat</div>
                            </div>
                        </div>
                        <blockquote className="mt-5 text-zinc-200 text-lg leading-relaxed">
                            "The 100 founders inside this book aren't rich, famous
                            or unicorn-hunting. They're patient, unfashionable,
                            and quietly changing what a business is allowed to
                            care about. That's the story I want on the shelf."
                        </blockquote>
                        <a
                            href={LINKEDIN_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="author-linkedin-post-link"
                            className="mt-6 inline-flex items-center gap-2 text-[#D4AF37] font-mono uppercase text-xs tracking-widest hover:text-[#F0C84B]"
                        >
                            <Linkedin size={14} /> Read on LinkedIn <ArrowRight size={12} />
                        </a>
                    </div>

                    <div className="md:col-span-2 rounded-2xl border border-white/10 p-8 flex flex-col justify-between">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">/in/buildwithbhat</div>
                            <p className="text-zinc-300 text-sm mt-4 leading-relaxed">
                                Chandrashekar writes about impact
                                entrepreneurship, founder conviction, and the
                                book-in-progress on LinkedIn every week.
                            </p>
                        </div>
                        <a
                            href={LINKEDIN_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="author-linkedin-follow-cta"
                            className="btn-gold mt-6"
                        >
                            <Linkedin size={14} /> Follow on LinkedIn
                        </a>
                    </div>
                </div>
            </section>
        </main>
    );
}
